import React, { useState } from 'react';
import axios from 'axios';
import { API, toast } from '../../App';
import { ArrowLeft, Save, AlertTriangle, CheckCircle2, FileText, Loader2, FileUp, X, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { LINKING_LEVELS } from './constants';

/**
 * CreateDossier - Formulaire de création d'un nouveau dossier SEO
 */
const CreateDossier = ({ eligibleSites, onCancel, onCreated }) => {
  const [form, setForm] = useState({
    title: '',
    objective: '',
    main_keyword: '',
    secondary_keywords: '',
    linking_level: 'standard',
    selected_site_ids: [],
    pilier_site_id: null,
    brief: '',
    source_urls: '',
    source_documents: [],
    custom_instructions: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const toggleSite = (id) => {
    setForm(f => {
      const isSelected = f.selected_site_ids.includes(id);
      let newSelectedIds;
      let newPilierId = f.pilier_site_id;
      
      if (isSelected) {
        newSelectedIds = f.selected_site_ids.filter(s => s !== id);
        if (f.pilier_site_id === id) {
          newPilierId = newSelectedIds.length > 0 ? newSelectedIds[0] : null;
        }
      } else {
        newSelectedIds = [...f.selected_site_ids, id];
        if (newSelectedIds.length === 1) {
          newPilierId = id;
        }
      }
      
      return {
        ...f,
        selected_site_ids: newSelectedIds,
        pilier_site_id: newPilierId
      };
    });
  };
  
  const setPilierSite = (id) => {
    if (form.selected_site_ids.includes(id)) {
      setForm(f => ({ ...f, pilier_site_id: id }));
    }
  };

  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploadingDoc(true);
    const newDocs = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await axios.post(`${API}/auto-dossiers/extract-document`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data.success) {
          newDocs.push({ filename: data.filename, content: data.content });
          toast.success(`${data.filename} : ${data.char_count.toLocaleString()} caractères extraits`);
        }
      } catch (err) {
        toast.error(`${file.name} : ${err.response?.data?.detail || 'Erreur extraction'}`);
      }
    }

    if (newDocs.length > 0) {
      setForm(f => ({ ...f, source_documents: [...f.source_documents, ...newDocs] }));
    }
    setUploadingDoc(false);
    e.target.value = '';
  };

  const removeDocument = (index) => {
    setForm(f => ({
      ...f,
      source_documents: f.source_documents.filter((_, i) => i !== index)
    }));
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error('Titre interne requis');
    if (!form.objective.trim()) return toast.error('Objectif requis');
    if (!form.main_keyword.trim()) return toast.error('Mot-clé principal requis');
    const secondaryList = form.secondary_keywords.split(',').map(s => s.trim()).filter(Boolean);
    if (secondaryList.length === 0) return toast.error('Au moins un mot-clé secondaire requis');
    if (form.selected_site_ids.length === 0) return toast.error('Sélectionnez au moins un site');
    if (!form.brief.trim()) return toast.error('Brief du pilier requis');
    const sourceUrls = (form.source_urls || '')
      .split(/[\n,]/)
      .map(s => s.trim())
      .filter(Boolean);
    
    if (sourceUrls.length === 0 && form.source_documents.length === 0) {
      return toast.error('Au moins une source requise (URL ou document)');
    }

    setSubmitting(true);
    try {
      const effectivePilierId = form.pilier_site_id || form.selected_site_ids[0] || null;
      
      let orderedSiteIds = [...form.selected_site_ids];
      if (effectivePilierId && orderedSiteIds.includes(effectivePilierId)) {
        orderedSiteIds = [effectivePilierId, ...orderedSiteIds.filter(id => id !== effectivePilierId)];
      }
      
      const payload = {
        title: form.title,
        objective: form.objective,
        main_keyword: form.main_keyword,
        secondary_keywords: secondaryList,
        linking_level: form.linking_level,
        selected_site_ids: orderedSiteIds,
        pilier_site_id: effectivePilierId,
        brief: form.brief,
        source_urls: sourceUrls,
        source_documents: form.source_documents,
        custom_instructions: form.custom_instructions || null,
      };
      const { data } = await axios.post(`${API}/auto-dossiers`, payload);
      const srcCount = sourceUrls.length + form.source_documents.length;
      toast.success(`Dossier créé — ${srcCount} source(s) enregistrée(s)`);
      onCreated(data.dossier.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Création échouée');
    } finally {
      setSubmitting(false);
    }
  };

  const eligible = eligibleSites.filter(s => s.is_eligible);
  const ineligible = eligibleSites.filter(s => !s.is_eligible);

  return (
    <div className="min-h-screen p-4 lg:p-8" data-testid="create-dossier-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Button onClick={onCancel} variant="outline" size="sm" data-testid="cancel-create-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour
          </Button>
          <h1 className="text-2xl font-bold text-cyan-300 font-rajdhani uppercase tracking-wider">Nouveau dossier</h1>
          <div />
        </div>

        <Card className="border-cyan-500/30 bg-slate-900/40 mb-4">
          <CardHeader><CardTitle className="text-sm text-cyan-300">Métadonnées</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-xs text-slate-400">Titre interne *</label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex: Dossier Retraite 2026" data-testid="dossier-title-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Objectif du dossier *</label>
              <Input value={form.objective} onChange={e => setForm({ ...form, objective: e.target.value })} placeholder="Ex: Capter le trafic sur 'réforme retraite 2026'" data-testid="dossier-objective-input" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400">Mot-clé principal *</label>
                <Input value={form.main_keyword} onChange={e => setForm({ ...form, main_keyword: e.target.value })} placeholder="Ex: réforme retraite" data-testid="dossier-keyword-input" />
              </div>
              <div>
                <label className="text-xs text-slate-400">Mots-clés secondaires * (virgule)</label>
                <Input value={form.secondary_keywords} onChange={e => setForm({ ...form, secondary_keywords: e.target.value })} placeholder="âge légal, cotisations, pension" data-testid="dossier-secondary-input" />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400">Niveau de maillage</label>
              <select
                value={form.linking_level}
                onChange={e => setForm({ ...form, linking_level: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200"
                data-testid="dossier-linking-select"
              >
                {LINKING_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400">Brief du pilier *</label>
              <Textarea value={form.brief} onChange={e => setForm({ ...form, brief: e.target.value })} rows={5} placeholder="Décrivez l'article pilier : sujet, angle global, points clés à couvrir, sources éventuelles…" data-testid="dossier-brief-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 flex items-center gap-2">
                URLs sources
                <span className="text-[10px] text-cyan-400/80 font-normal">1 par ligne ou séparées par virgule — scrapées au lancement des articles</span>
              </label>
              <Textarea
                value={form.source_urls}
                onChange={e => setForm({ ...form, source_urls: e.target.value })}
                rows={3}
                placeholder={"https://www.lemonde.fr/article-1\nhttps://www.lesechos.fr/dossier-pension-2026"}
                data-testid="dossier-source-urls-input"
                className="font-mono text-xs"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 flex items-center gap-2">
                Documents sources
                <span className="text-[10px] text-cyan-400/80 font-normal">PDF, DOCX, DOC, TXT — extraits immédiatement</span>
              </label>
              <div className="mt-2">
                <label className={`inline-flex items-center gap-2 px-3 py-2 rounded border border-dashed cursor-pointer transition ${uploadingDoc ? 'border-amber-500/50 bg-amber-500/10' : 'border-slate-600 hover:border-cyan-500/50 hover:bg-slate-800/50'}`}>
                  {uploadingDoc ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
                      <span className="text-sm text-amber-300">Extraction en cours...</span>
                    </>
                  ) : (
                    <>
                      <FileUp className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-300">Ajouter des documents</span>
                    </>
                  )}
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.docx,.doc,.txt,.rtf,.odt"
                    onChange={handleDocumentUpload}
                    className="hidden"
                    disabled={uploadingDoc}
                    data-testid="dossier-document-upload"
                  />
                </label>
              </div>
              {form.source_documents.length > 0 && (
                <div className="mt-3 space-y-2">
                  {form.source_documents.map((doc, i) => (
                    <div key={`doc-${i}-${doc.filename}`} className="flex items-center gap-2 p-2 rounded bg-slate-800/50 border border-slate-700">
                      <FileText className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-200 truncate">{doc.filename}</div>
                        <div className="text-[10px] text-slate-500">{doc.content.length.toLocaleString()} caractères</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDocument(i)}
                        className="p-1 rounded hover:bg-red-500/20 text-slate-400 hover:text-red-400 transition"
                        title="Supprimer"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {form.source_urls.trim() === '' && form.source_documents.length === 0 && (
                <p className="text-xs text-amber-400/80 mt-2">* Au moins une source requise (URL ou document)</p>
              )}
            </div>
            <div>
              <label className="text-xs text-slate-400">Consignes supplémentaires (optionnel)</label>
              <Textarea value={form.custom_instructions} onChange={e => setForm({ ...form, custom_instructions: e.target.value })} rows={3} placeholder="Contraintes éditoriales, ton, exemples obligatoires…" data-testid="dossier-instructions-input" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-cyan-500/30 bg-slate-900/40 mb-4">
          <CardHeader>
            <CardTitle className="text-sm text-cyan-300 flex items-center justify-between">
              <span>Sites cibles ({form.selected_site_ids.length} sélectionné(s))</span>
              <span className="text-xs text-slate-500">{eligible.length} éligible(s) / {eligibleSites.length}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {eligible.length === 0 && (
              <div className="text-sm text-amber-400 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Aucun site éligible. Complétez pitch, prompt, connexion WP et mapping auteur dans WordPress + Profil.
              </div>
            )}
            {form.selected_site_ids.length > 1 && (
              <div className="text-xs text-slate-400 mb-3 flex items-center gap-2">
                <Star className="w-3 h-3 text-amber-400" />
                <span>Cliquez sur l&apos;étoile pour définir le site pilier</span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="eligible-sites-list">
              {eligible.map(s => {
                const isSelected = form.selected_site_ids.includes(s.id);
                const isPilier = form.pilier_site_id === s.id || (form.pilier_site_id === null && form.selected_site_ids[0] === s.id);
                return (
                <div key={s.id} className={`flex items-center gap-2 p-2 rounded border transition ${isSelected ? (isPilier ? 'border-amber-400 bg-amber-500/10' : 'border-cyan-400 bg-cyan-500/10') : 'border-slate-700 hover:border-cyan-500/40'}`} data-testid={`site-toggle-${s.id}`}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSite(s.id)}
                    className="accent-cyan-500 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleSite(s.id)}>
                    <div className="text-sm text-slate-200 truncate flex items-center gap-1">
                      {s.name}
                      {isSelected && isPilier && (
                        <span className="text-[10px] bg-amber-500/30 text-amber-300 px-1.5 py-0.5 rounded ml-1">PILIER</span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500 truncate">{s.pitch || '—'}</div>
                  </div>
                  {isSelected && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setPilierSite(s.id); }}
                      className={`p-1 rounded transition ${isPilier ? 'text-amber-400' : 'text-slate-500 hover:text-amber-300'}`}
                      title={isPilier ? 'Site pilier' : 'Définir comme pilier'}
                    >
                      <Star className={`w-4 h-4 ${isPilier ? 'fill-amber-400' : ''}`} />
                    </button>
                  )}
                  {!isSelected && (
                    <CheckCircle2 className="w-4 h-4 text-slate-600 flex-shrink-0" />
                  )}
                </div>
              )})}
            </div>
            {ineligible.length > 0 && (
              <details className="mt-4">
                <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-300">Voir {ineligible.length} site(s) non éligible(s)</summary>
                <div className="mt-2 space-y-1">
                  {ineligible.map(s => (
                    <div key={s.id} className="text-xs text-slate-500 p-1.5 rounded bg-slate-800/40">
                      <span className="text-slate-300">{s.name}</span>
                      <span className="text-amber-400 ml-2">— {s.blocking_reasons.join(', ')}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button onClick={onCancel} variant="outline" data-testid="cancel-bottom-btn">Annuler</Button>
          <Button onClick={submit} disabled={submitting} className="bg-cyan-600 hover:bg-cyan-500" data-testid="submit-dossier-btn">
            {submitting ? 'Création…' : <><Save className="w-4 h-4 mr-2" /> Créer & ouvrir</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateDossier;
